import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Settings, 
  Moon, 
  Sun, 
  Maximize, 
  BarChart3, 
  Copy, 
  Palette, 
  Hash, 
  Zap, 
  History, 
  Download, 
  Shuffle 
} from 'lucide-react';

// Types
interface RgbColor {
  r: number;
  g: number;
  b: number;
}

interface ConversionHistory {
  id: number;
  hex: string;
  rgb: RgbColor;
  timestamp: string;
  date: string;
}

type OutputFormat = 'standard' | 'css' | 'array' | 'object';

const HexToRgbConverter: React.FC = () => {
  // State with proper types
  const [isDark, setIsDark] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [hexInput, setHexInput] = useState<string>('#3B82F6');
  const [rgbOutput, setRgbOutput] = useState<RgbColor>({ r: 59, g: 130, b: 246 });
  const [conversionHistory, setConversionHistory] = useState<ConversionHistory[]>([]);
  const [autoConvert, setAutoConvert] = useState<boolean>(true);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('standard');
  const [copyFeedback, setCopyFeedback] = useState<string>('');
  const [validationError, setValidationError] = useState<string>('');

  // HEX to RGB conversion function
  const hexToRgb = useCallback((hex: string): RgbColor | null => {
    // Remove # if present
    const cleanHex = hex.replace('#', '');
    
    // Validate hex format
    if (!/^[0-9A-Fa-f]{6}$/.test(cleanHex) && !/^[0-9A-Fa-f]{3}$/.test(cleanHex)) {
      return null;
    }
    
    // Handle 3-digit hex
    let fullHex = cleanHex;
    if (cleanHex.length === 3) {
      fullHex = cleanHex.split('').map(char => char + char).join('');
    }
    
    const r = parseInt(fullHex.substring(0, 2), 16);
    const g = parseInt(fullHex.substring(2, 4), 16);
    const b = parseInt(fullHex.substring(4, 6), 16);
    
    return { r, g, b };
  }, []);

  // Validate hex input
  const validateHex = useCallback((hex: string): boolean => {
    const cleanHex = hex.replace('#', '');
    const isValid = /^[0-9A-Fa-f]{6}$/.test(cleanHex) || /^[0-9A-Fa-f]{3}$/.test(cleanHex);
    
    if (!isValid && hex.length > 0) {
      setValidationError('Invalid HEX format. Use #RRGGBB or #RGB');
    } else {
      setValidationError('');
    }
    
    return isValid;
  }, []);

  // Format RGB output based on selected format
  const formatRgbOutput = useCallback((rgb: RgbColor, format: OutputFormat): string => {
    switch (format) {
      case 'css':
        return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
      case 'array':
        return `[${rgb.r}, ${rgb.g}, ${rgb.b}]`;
      case 'object':
        return `{r: ${rgb.r}, g: ${rgb.g}, b: ${rgb.b}}`;
      default:
        return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
    }
  }, []);

  // Handle conversion
  const handleConvert = useCallback(() => {
    validateHex(hexInput);
    const result = hexToRgb(hexInput);
    if (result) {
      setRgbOutput(result);
      
      // Add to history
      const newConversion: ConversionHistory = {
        id: Date.now(),
        hex: hexInput.startsWith('#') ? hexInput : `#${hexInput}`,
        rgb: result,
        timestamp: new Date().toLocaleTimeString(),
        date: new Date().toLocaleDateString()
      };
      
      setConversionHistory(prev => [newConversion, ...prev.slice(0, 19)]);
    }
  }, [hexInput, hexToRgb, validateHex]);

  // Auto convert when input changes
  useEffect(() => {
    if (autoConvert && hexInput) {
      const timer = setTimeout(() => {
        validateHex(hexInput);
        const result = hexToRgb(hexInput);
        if (result) {
          setRgbOutput(result);
          
          // Add to history
          const newConversion: ConversionHistory = {
            id: Date.now(),
            hex: hexInput.startsWith('#') ? hexInput : `#${hexInput}`,
            rgb: result,
            timestamp: new Date().toLocaleTimeString(),
            date: new Date().toLocaleDateString()
          };
          
          setConversionHistory(prev => [newConversion, ...prev.slice(0, 19)]);
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [hexInput, autoConvert, hexToRgb, validateHex]);

  // Copy to clipboard with feedback
  const copyToClipboard = useCallback(async (text: string, label: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback(`${label} copied!`);
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      setCopyFeedback('Failed to copy');
      setTimeout(() => setCopyFeedback(''), 2000);
    }
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(async (): Promise<void> => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
    }
  }, []);

  // Generate random hex color
  const generateRandomHex = useCallback((): void => {
    const randomColor = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
    setHexInput(randomColor);
  }, []);

  // Get color name approximation
  const getColorName = useCallback((hex: string): string => {
    const rgb = hexToRgb(hex);
    if (!rgb) return 'Unknown';
    
    if (rgb.r > 200 && rgb.g < 100 && rgb.b < 100) return 'Red-ish';
    if (rgb.g > 200 && rgb.r < 100 && rgb.b < 100) return 'Green-ish';
    if (rgb.b > 200 && rgb.r < 100 && rgb.g < 100) return 'Blue-ish';
    if (rgb.r > 200 && rgb.g > 200 && rgb.b < 100) return 'Yellow-ish';
    if (rgb.r < 50 && rgb.g < 50 && rgb.b < 50) return 'Dark';
    if (rgb.r > 200 && rgb.g > 200 && rgb.b > 200) return 'Light';
    
    return 'Mixed';
  }, [hexToRgb]);

  // Export history as CSV
  const exportHistory = useCallback((): void => {
    const data = conversionHistory.map(h => `${h.hex},${h.rgb.r},${h.rgb.g},${h.rgb.b}`).join('\n');
    const blob = new Blob([`HEX,R,G,B\n${data}`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'hex-to-rgb-history.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [conversionHistory]);

  // Computed values
  const currentRgbFormatted = formatRgbOutput(rgbOutput, outputFormat);
  const currentHex = hexInput.startsWith('#') ? hexInput : `#${hexInput}`;
  const todaysConversions = conversionHistory.filter(
    h => new Date(h.date).toDateString() === new Date().toDateString()
  ).length;
  const uniqueColors = new Set(conversionHistory.map(h => h.hex)).size;

  return (
    <>
      <Helmet>
        <title>HEX to RGB Converter - Free Online Color Converter Tool</title>
        <meta 
          name="description" 
          content="Convert HEX color codes to RGB values instantly. Free online HEX to RGB converter with color picker, validation, and multiple output formats. Perfect for web developers and designers." 
        />
        <meta 
          name="keywords" 
          content="hex to rgb, color converter, hex converter, rgb converter, color picker, web colors, css colors, color codes, design tools" 
        />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="HEX to RGB Converter - Free Online Color Tool" />
        <meta 
          property="og:description" 
          content="Convert HEX color codes to RGB values instantly with our free online converter. Features color picker, validation, and multiple output formats." 
        />
        <meta 
          property="og:image" 
          content="https://via.placeholder.com/1200x630/3B82F6/FFFFFF?text=HEX+to+RGB+Converter" 
        />
        <meta property="og:site_name" content="Color Converter Tools" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="HEX to RGB Converter - Free Online Tool" />
        <meta 
          name="twitter:description" 
          content="Convert HEX colors to RGB format instantly. Free tool with color picker and validation." 
        />
        <meta 
          name="twitter:image" 
          content="https://via.placeholder.com/1200x630/3B82F6/FFFFFF?text=HEX+to+RGB+Converter" 
        />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Color Converter Tools" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "HEX to RGB Converter",
            "description": "Free online tool to convert HEX color codes to RGB values with color picker and validation",
            "applicationCategory": "DesignApplication",
            "operatingSystem": "Any",
            "permissions": "browser",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "HEX to RGB conversion",
              "Color picker integration",
              "Multiple output formats",
              "Conversion history",
              "Real-time validation",
              "Export functionality"
            ]
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen transition-all duration-300 ${isDark ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-green-900 dark:from-gray-900 dark:via-black dark:to-green-800">
          
          {/* Header */}
          <header className="bg-black/20 backdrop-blur-sm border-b border-white/10 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Hash className="w-8 h-8 text-green-400" />
                <h1 className="text-2xl font-bold text-white">HEX to RGB Converter</h1>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                  title="Analytics"
                  type="button"
                >
                  <BarChart3 className="w-5 h-5" />
                </button>
                
                <button
                  onClick={() => setIsDark(!isDark)}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                  title="Toggle Theme"
                  type="button"
                >
                  {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                  title="Settings"
                  type="button"
                >
                  <Settings className="w-5 h-5" />
                </button>
                
                <button
                  onClick={toggleFullscreen}
                  className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
                  title="Fullscreen"
                  type="button"
                >
                  <Maximize className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Copy Feedback */}
            {copyFeedback && (
              <div className="mt-2 text-center">
                <span className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-sm">
                  {copyFeedback}
                </span>
              </div>
            )}
          </header>

          <div className="p-6">
            {/* Analytics Panel */}
            {showAnalytics && (
              <div className="mb-6 bg-black/30 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2" />
                  Analytics Dashboard
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-green-500/20 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-400">{conversionHistory.length}</div>
                    <div className="text-green-200 text-sm">Total Conversions</div>
                  </div>
                  <div className="bg-blue-500/20 rounded-lg p-4">
                    <div className="text-2xl font-bold text-blue-400">{todaysConversions}</div>
                    <div className="text-blue-200 text-sm">Today's Conversions</div>
                  </div>
                  <div className="bg-purple-500/20 rounded-lg p-4">
                    <div className="text-2xl font-bold text-purple-400">{uniqueColors}</div>
                    <div className="text-purple-200 text-sm">Unique Colors</div>
                  </div>
                  <div className="bg-yellow-500/20 rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-400">HEX→RGB</div>
                    <div className="text-yellow-200 text-sm">Conversion Type</div>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Panel */}
            {showSettings && (
              <div className="mb-6 bg-black/30 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  Advanced Settings
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label htmlFor="outputFormat" className="block text-white mb-2">
                      Output Format
                    </label>
                    <select
                      id="outputFormat"
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value as OutputFormat)}
                      className="w-full p-3 rounded-lg bg-white/10 border border-white/20 text-white"
                    >
                      <option value="standard">Standard (255, 255, 255)</option>
                      <option value="css">CSS (rgb(255, 255, 255))</option>
                      <option value="array">Array [255, 255, 255]</option>
                      <option value="object">Object (r: 255, g: 255, b: 255)</option>
                    </select>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      id="autoConvert"
                      checked={autoConvert}
                      onChange={(e) => setAutoConvert(e.target.checked)}
                      className="w-4 h-4 text-green-600 rounded"
                    />
                    <label htmlFor="autoConvert" className="text-white">Auto Convert</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setConversionHistory([])}
                      className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors"
                      type="button"
                    >
                      Clear History
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Main Converter */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input Section */}
              <div className="bg-black/30 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                  <Hash className="w-5 h-5 mr-2" />
                  HEX Input
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="hexInput" className="block text-white mb-2">
                      HEX Color Code
                    </label>
                    <div className="flex space-x-2">
                      <input
                        id="hexInput"
                        type="text"
                        value={hexInput}
                        onChange={(e) => setHexInput(e.target.value)}
                        className="flex-1 p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 font-mono"
                        placeholder="#RRGGBB or #RGB"
                      />
                      <input
                        type="color"
                        value={currentHex}
                        onChange={(e) => setHexInput(e.target.value)}
                        className="w-16 h-12 rounded-lg border border-white/20 cursor-pointer"
                      />
                      <button
                        onClick={generateRandomHex}
                        className="px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                        title="Random Color"
                        type="button"
                      >
                        <Shuffle className="w-5 h-5" />
                      </button>
                    </div>
                    {validationError && (
                      <p className="text-red-400 text-sm mt-2" role="alert">
                        {validationError}
                      </p>
                    )}
                  </div>

                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="text-white/70 text-sm mb-2">Color Preview</div>
                    <div className="flex items-center space-x-4">
                      <div 
                        className="w-16 h-16 rounded-lg border border-white/20"
                        style={{ backgroundColor: currentHex }}
                        aria-label={`Color preview: ${currentHex}`}
                      />
                      <div>
                        <div className="text-white font-semibold">{getColorName(hexInput)}</div>
                        <div className="text-white/70 text-sm">Approximate color name</div>
                      </div>
                    </div>
                  </div>

                  {!autoConvert && (
                    <button
                      onClick={handleConvert}
                      className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
                      type="button"
                    >
                      <Zap className="w-5 h-5" />
                      <span>Convert to RGB</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Output Section */}
              <div className="bg-black/30 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                  <Palette className="w-5 h-5 mr-2" />
                  RGB Output
                </h2>
                
                <div className="space-y-4">
                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white/70">RGB Values</span>
                      <button
                        onClick={() => copyToClipboard(currentRgbFormatted, 'RGB')}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        title="Copy RGB values"
                        type="button"
                      >
                        <Copy className="w-4 h-4 text-green-400" />
                      </button>
                    </div>
                    <div className="text-white font-mono text-lg">{currentRgbFormatted}</div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-red-500/20 rounded-lg p-3 text-center">
                      <div className="text-red-300 font-semibold text-lg">{rgbOutput.r}</div>
                      <div className="text-red-200 text-sm">Red</div>
                    </div>
                    <div className="bg-green-500/20 rounded-lg p-3 text-center">
                      <div className="text-green-300 font-semibold text-lg">{rgbOutput.g}</div>
                      <div className="text-green-200 text-sm">Green</div>
                    </div>
                    <div className="bg-blue-500/20 rounded-lg p-3 text-center">
                      <div className="text-blue-300 font-semibold text-lg">{rgbOutput.b}</div>
                      <div className="text-blue-200 text-sm">Blue</div>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-lg p-4">
                    <div className="text-white/70 text-sm mb-2">All Formats</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-white/70">Standard:</span>
                        <span className="text-white font-mono">{formatRgbOutput(rgbOutput, 'standard')}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-white/70">CSS:</span>
                        <span className="text-white font-mono">{formatRgbOutput(rgbOutput, 'css')}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-white/70">Array:</span>
                        <span className="text-white font-mono">{formatRgbOutput(rgbOutput, 'array')}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-white/70">Object:</span>
                        <span className="text-white font-mono">{formatRgbOutput(rgbOutput, 'object')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Conversion History */}
            {conversionHistory.length > 0 && (
              <div className="mt-6 bg-black/30 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white flex items-center">
                    <History className="w-5 h-5 mr-2" />
                    Conversion History
                  </h3>
                  <div className="flex items-center space-x-2">
                    <span className="text-white/70 text-sm">{conversionHistory.length} conversions</span>
                    <button
                      onClick={exportHistory}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="Export as CSV"
                      type="button"
                    >
                      <Download className="w-4 h-4 text-green-400" />
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
                  {conversionHistory.map((conversion) => (
                    <div key={conversion.id} className="bg-white/5 rounded-lg p-3 hover:bg-white/10 transition-colors">
                      <div className="flex items-center space-x-3 mb-2">
                        <div 
                          className="w-8 h-8 rounded border border-white/20"
                          style={{ backgroundColor: conversion.hex }}
                          aria-label={`Color: ${conversion.hex}`}
                        />
                        <div>
                          <div className="text-white font-mono text-sm">{conversion.hex}</div>
                          <div className="text-green-300 font-mono text-xs">
                            rgb({conversion.rgb.r}, {conversion.rgb.g}, {conversion.rgb.b})
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-white/50 text-xs">{conversion.timestamp}</div>
                        <button
                          onClick={() => copyToClipboard(
                            `rgb(${conversion.rgb.r}, ${conversion.rgb.g}, ${conversion.rgb.b})`,
                            'RGB'
                          )}
                          className="p-1 hover:bg-white/10 rounded transition-colors"
                          type="button"
                          title="Copy RGB values"
                        >
                          <Copy className="w-3 h-3 text-green-400" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default HexToRgbConverter;